import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Theme, styled } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import apiRoutes from '../../../../../config/api/routes';
import {
  CurriculumGoalType,
  IBookCurriculumGoal,
} from '../../../../../config/api/models/CurriculumGoal';
import { useFetch } from '../../../../shared/hooks';
import { Message } from '../../../../shared/components';
import { StyledWrapper } from '../Atoms';

const StyledCombobox = styled(TextField)<Theme>(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: theme.config.defaultBorder(),
  borderRadius: theme.config.defaultBorderRadius,
  boxShadow: theme.config.defaultBoxShadow,
  padding: theme.spacing(1, 2),
  fontSize: theme.typography.h3.fontSize,
  color: theme.palette.grey['900'],
  width: '100%',
  '& label': {
    display: 'none',
  },
  '& label + .MuiInput-formControl': {
    marginTop: 0,
  },
  '& .MuiAutocomplete-inputRoot:before': {
    display: 'none',
  },
}));

const StyledGoalId = styled('span')<Theme>(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const getAvailableCurriculumGoals = (
  allGoals: IBookCurriculumGoal[],
  addedGoals: IBookCurriculumGoal[]
) =>
  allGoals.filter(
    (allGoalsItem) => !addedGoals.find((addedGoalsItem) => addedGoalsItem.id === allGoalsItem.id)
  );

type Props = {
  addedGoals: IBookCurriculumGoal[];
  bookId?: string;
  type: CurriculumGoalType;
  onAddGoal: (goal: IBookCurriculumGoal) => void;
};
const CurriculumGoalsByTypeCombobox: FC<Props> = ({ addedGoals, bookId, type, onAddGoal }) => {
  const { t } = useTranslation();

  const [getCurriculumGoalsByBookResponse, getCurriculumGoalsByBook] = useFetch<IBookCurriculumGoal[]>('');
  useEffect(() => {
    if (!bookId) return;
    getCurriculumGoalsByBook({ path: apiRoutes.curriculumGoalsByBook(bookId, type) });
  }, [bookId, getCurriculumGoalsByBook, type]);

  const handleChange = (e: any, value: any) => {
    onAddGoal(value as IBookCurriculumGoal);
  };

  const availableCurriculumGoals = useMemo(() => {
    if (!getCurriculumGoalsByBookResponse.success) return [];
    return getAvailableCurriculumGoals(getCurriculumGoalsByBookResponse.data, addedGoals);
  }, [addedGoals, getCurriculumGoalsByBookResponse]);

  return (
    <StyledWrapper>
      <Autocomplete
        id={`curriculum-goals-${type}-combobox`}
        options={availableCurriculumGoals}
        getOptionLabel={(option) => `${option.goalId} - ${option.endTerm}`}
        renderInput={(params) => (
          <StyledCombobox {...params} label={t('trajectory.sidebar.curriculumGoals.placeholder')} />
        )}
        onChange={handleChange}
        renderOption={(option, { inputValue }) => {
          const matches = match(option.endTerm, inputValue);
          const parts = parse(option.endTerm, matches);

          return (
            <div>
              <StyledGoalId>{option.goalId}</StyledGoalId>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  <span>{part.text}</span>
                </span>
              ))}
            </div>
          );
        }}
      />
      {getCurriculumGoalsByBookResponse.pending && t('shared.loading')}
      {getCurriculumGoalsByBookResponse.error && (
        <Message type="error">{getCurriculumGoalsByBookResponse.reason}</Message>
      )}
    </StyledWrapper>
  );
};

export default CurriculumGoalsByTypeCombobox;
